/**
 * ============================================================================
 * expensePresets — Plantillas de gastos por Incoterm (Fase 1, frontend-only)
 * ============================================================================
 * Catálogo determinístico de gastos típicos por término comercial. El usuario
 * carga una plantilla desde el simulador y los gastos se inyectan en
 * `sim_params.gastos_nuevos` (cero cambios al motor).
 *
 * Fuente: práctica común en importaciones marítimas Colombia (Buenaventura /
 * Cartagena / Santa Marta). Montos en USD están como referencia razonable y
 * deben editarse caso a caso. Montos COP típicos para una operación FCL.
 * ============================================================================
 */

export type Incoterm = "EXW" | "FOB" | "FCA" | "CIF" | "CFR" | "DAP" | "DDP";

export type PresetScope = "origen" | "internacional" | "destino";
export type PresetMoneda = "USD" | "COP" | "EUR";
export type PresetBase = "Peso" | "Valor" | "CBM" | "Ninguna";

export interface PresetGasto {
  /** Concepto canónico del gasto. */
  concepto: string;
  /** Moneda del costo. */
  moneda: PresetMoneda;
  /** Base de prorrateo recomendada para el motor de landed cost. */
  base_prorrateo: PresetBase;
  /** Monto sugerido por defecto (0 ⇒ obliga al usuario a llenar). */
  monto_default: number;
  /** Si suma a Base Gravable (CIF aduanero) para Arancel + IVA. */
  incluir_en_base_gravable: boolean;
  /** Origen / Internacional / Destino — usado por la hoja "Desglose de Gastos". */
  scope: PresetScope;
  /** Etiqueta corta de ayuda para el usuario. */
  hint?: string;
}

const FOB: PresetGasto[] = [
  // Internacional
  { concepto: "Flete marítimo internacional",        moneda: "USD", base_prorrateo: "Peso",  monto_default: 0,       incluir_en_base_gravable: true,  scope: "internacional", hint: "Origen → puerto destino" },
  { concepto: "Seguro de carga internacional",       moneda: "USD", base_prorrateo: "Valor", monto_default: 0,       incluir_en_base_gravable: true,  scope: "internacional", hint: "≈ 0.3% del valor FOB" },
  { concepto: "ISPS / Recargo seguridad",            moneda: "USD", base_prorrateo: "Valor", monto_default: 0,       incluir_en_base_gravable: true,  scope: "internacional" },
  // Destino
  { concepto: "THC destino (manipuleo en puerto)",   moneda: "COP", base_prorrateo: "Peso",  monto_default: 0,  incluir_en_base_gravable: false, scope: "destino" },
  { concepto: "Agenciamiento aduanero",              moneda: "COP", base_prorrateo: "Valor", monto_default: 0, incluir_en_base_gravable: false, scope: "destino" },
  { concepto: "Elaboración DIM + Declaración Andina",moneda: "COP", base_prorrateo: "Valor", monto_default: 0,  incluir_en_base_gravable: false, scope: "destino" },
  { concepto: "Bodegaje (días libres en destino)",   moneda: "COP", base_prorrateo: "Peso",  monto_default: 0,       incluir_en_base_gravable: false, scope: "destino", hint: "Solo si excede días libres" },
  { concepto: "Papelería y conexión sistema",        moneda: "COP", base_prorrateo: "Valor", monto_default: 0,   incluir_en_base_gravable: false, scope: "destino" },
];

const EXW: PresetGasto[] = [
  { concepto: "Cargue en planta origen",             moneda: "USD", base_prorrateo: "Peso",  monto_default: 0,       incluir_en_base_gravable: true,  scope: "origen" },
  { concepto: "Transporte interno origen",           moneda: "USD", base_prorrateo: "Peso",  monto_default: 0,       incluir_en_base_gravable: true,  scope: "origen" },
  { concepto: "Manipuleo y tarifas portuarias origen", moneda: "USD", base_prorrateo: "Valor", monto_default: 0,    incluir_en_base_gravable: true,  scope: "origen" },
  { concepto: "Documentación de exportación",        moneda: "USD", base_prorrateo: "Valor", monto_default: 0,     incluir_en_base_gravable: true,  scope: "origen" },
  ...FOB,
];

const FCA: PresetGasto[] = [
  { concepto: "Manipuleo y tarifas portuarias origen", moneda: "USD", base_prorrateo: "Valor", monto_default: 0,    incluir_en_base_gravable: true,  scope: "origen" },
  ...FOB,
];

const CFR: PresetGasto[] = FOB.filter((g) => g.concepto !== "Seguro de carga internacional");

const CIF: PresetGasto[] = FOB.filter(
  (g) =>
    g.concepto !== "Flete marítimo internacional" &&
    g.concepto !== "Seguro de carga internacional",
);

const DAP: PresetGasto[] = [
  { concepto: "Agenciamiento aduanero",              moneda: "COP", base_prorrateo: "Valor", monto_default: 0, incluir_en_base_gravable: false, scope: "destino" },
  { concepto: "Elaboración DIM + Declaración Andina",moneda: "COP", base_prorrateo: "Valor", monto_default: 0,  incluir_en_base_gravable: false, scope: "destino" },
  { concepto: "Bodegaje (días libres en destino)",   moneda: "COP", base_prorrateo: "Peso",  monto_default: 0,       incluir_en_base_gravable: false, scope: "destino" },
  { concepto: "Papelería y conexión sistema",        moneda: "COP", base_prorrateo: "Valor", monto_default: 0,   incluir_en_base_gravable: false, scope: "destino" },
];

const DDP: PresetGasto[] = []; // todo cubierto por proveedor

export const PRESETS_BY_INCOTERM: Record<Incoterm, PresetGasto[]> = {
  EXW,
  FOB,
  FCA,
  CIF,
  CFR,
  DAP,
  DDP,
};

export const INCOTERM_LABELS: Record<Incoterm, string> = {
  EXW: "EXW — Ex Works",
  FOB: "FOB — Free On Board",
  FCA: "FCA — Free Carrier",
  CIF: "CIF — Cost, Insurance & Freight",
  CFR: "CFR — Cost & Freight",
  DAP: "DAP — Delivered At Place",
  DDP: "DDP — Delivered Duty Paid",
};

/** Sugerencias por defecto de "posibles sobrecostos" para la cotización. */
export const DEFAULT_SOBRECOSTOS: string[] = [
  "Bodegaje adicional si excede los días libres en destino.",
  "Demurrage / detention de contenedores por demoras en devolución.",
  "Inspección antinarcóticos o reaforo DIAN.",
  "Variación de la TRM entre fecha de cotización y fecha de pago de aranceles.",
  "Movimientos extraordinarios (traslado a depósito, reposicionamiento).",
];

/**
 * Mapa global de concepto → scope a partir de TODOS los presets (cualquier
 * incoterm). Permite que el ExpenseManagerSheet y el export Excel resuelvan
 * el scope correctamente aunque el usuario cambie el incoterm o el gasto
 * provenga de OCR sin haberse cargado desde plantilla.
 */
export const SCOPE_BY_CONCEPT: Record<string, PresetScope> = (() => {
  const out: Record<string, PresetScope> = {};
  for (const presets of Object.values(PRESETS_BY_INCOTERM)) {
    for (const p of presets) {
      if (!(p.concepto in out)) out[p.concepto] = p.scope;
    }
  }
  return out;
})();

/**
 * Resuelve el scope de un gasto. Prioridad:
 *   1. Match exacto contra SCOPE_BY_CONCEPT.
 *   2. Heurística por keywords en el concepto (cubre OCR / textos libres).
 *   3. Fallback: "destino".
 */
export function inferScope(concepto: string | null | undefined): PresetScope {
  const raw = (concepto ?? "").trim();
  if (!raw) return "destino";
  const exact = SCOPE_BY_CONCEPT[raw];
  if (exact) return exact;
  const k = raw.toLowerCase();
  // Internacional: flete + seguro internacional / ISPS / BAF
  if (
    /(flete\s+(mar[ií]timo|a[eé]reo|internacional))|(seguro.*(carga|internacional))|isps|baf|recargo\s+seguridad|env[ií]o\s+internacional/.test(k)
  ) return "internacional";
  // Origen: cargue planta, transporte/manipuleo origen, doc exportación
  if (
    /(cargue.*planta)|(transporte\s+interno)|(manipuleo.*origen)|(tarifa.*portuaria.*origen)|(documentaci[oó]n.*exportaci[oó]n)|(handling.*origen)|(thc.*origen)/.test(k)
  ) return "origen";
  // Destino fallback
  return "destino";
}

